<template>
    <div v-if="minMaxRange">
        <div class="d-flex justify-content-between align-items-baseline">
            <h4>Price</h4>
            <form-select v-if="(uiStore.isMobile || uiStore.isTablet) && quotes" :options="priceTypeOptions" v-model="priceType" @change="resetPricing"  style="max-width:120px" size="sm"/>
        </div>
        <form-group>
            <form-duel-range :min="minMaxRange.start" :max="minMaxRange.end" v-model="priceRange" :track="false" :showValues="false" size="lg" class="border-0 mt-5" @input="inputPriceRangeChange" :resetValue="resetValue">
                <div v-if="tickHeights" class="d-flex align-items-end w-100 position-absolute bottom-0 mb-2" style="height: 50px;">
                    <div v-for="(tick, index) in ticks" :key="`price-range-tick-${index}`" class="flex-fill price-chart-search-box h-100 d-flex align-items-end" :class="`border-bottom border-${tickColors[index]}`" @click="() => setFromIndex(index)">
                        <div :class="`bg-${tickColors[index]}`" class="xx-small w-100 rounded-top-1" :style="`height:${tickHeights[index]}%`">&nbsp;</div>
                    </div>
                </div>
            </form-duel-range>
        </form-group>

        <div class="d-flex align-items-center justify-content-center w-100 mt-3">
            <form-floating-label class="w-auto w-md-40">
                <template #default>
                    <form-number :money="true" name="minimum-price" v-model="priceMin" @input="priceMinChange" :decimalPlaces="0" />
                </template>
                <template #label>
                    {{ minPriceLabel }}
                </template>
            </form-floating-label>
            <div class="px-2 px-md-3 text-muted">
                -
            </div>
            <form-floating-label class="w-auto w-md-40">
                <template #default>
                    <form-number :money="true" name="maximum-price" v-model="priceMax" @input="priceMaxChange" :decimalPlaces="0" />
                </template>
                <template #label>
                    {{ maxPriceLabel }}
                </template>
            </form-floating-label>

            <template v-if="!uiStore.isMobile && !uiStore.isTablet && quotes">
                <div class="px-3 text-muted">
                    -
                </div>
                <form-select :options="priceTypeOptions" v-model="priceType" class="w-auto w-md-20 py-3" @change="resetPricing"/>
            </template>
        </div>

        <hr class="mb-5 mt-4" />
    </div>
    
</template>

<script type="text/javascript">
    import CommonComponents from '@/components/Common.vue';

    import FormOption from '@/classes/FormOption.js';
    import Formatter from '@/classes/Formatter.js';
    import Range from '@/classes/Ranges/Range.js';
    import { UseUIStore } from '@/store/UI.js';

    export default {
        components: {
            ...CommonComponents,
        },

        setup() {
            return {
                uiStore: UseUIStore(),
            };
        },

        data() {
            return {
                priceMin: null,
                priceMax: null,
                priceRange: null,
                priceType: null,

                // resetting the range values
                // when we change the pricing type
                resetValue: 0,

                priceTypeOptions: FormOption.fromKeyValue({night: 'Per Night', stay: 'Per Stay'}),
            };
        },

        emits: ['update:modelValue'],

        created() {
            if (this.modelValue) {
                this.setSearchData();
            }

            if ((this.quotes) && (!this.priceType)) {
                this.priceType = 'stay';
            }

            this.$watch(
                () => this.modelValue,
                this.setSearchData,
                { deep: true }
            );
        },

        props: {
            modelValue: {
                type: Object,
                default: null,
            },

            // a pre-computed list of properties
            /**
             * IMPORTANT, the properties in this response
             * will have truncated keys
             * e.g. instead of "name", it'll be "n"
             * 'name'                  =>  'n',
             * 'slug'                  =>  'sg',
             * 'city_state_country'    =>  'c',
             * 'market_id'             =>  'mId',
             * 'bedrooms'              =>  'bd',
             * 'bathrooms'             =>  'bt',
             * 'service_level'         =>  'sl',
             * 'max_occupancy'         =>  'oc',
             * 'schedule_price_avg'    =>  'avg',
             * 'public_lat'            =>  'lt',
             * 'public_lng'            =>  'lg',
             * 'pet_policy'            =>  'pt',
             */
            properties: {
                type: Array,
                required: true,
            },

            // object keyed by the property id
            quotes: {
                type: Object,
                default: null,
            },

            quotesNightly: {
                type: Object,
                default: null,
            },
        },

        watch: {
            quotes() {
                if (this.quotes) {
                    if (!this.priceType) {
                        this.priceType = 'stay';
                        this.priceRange = null;
                        this.priceMin = null;
                        this.priceMax = null;
                    }
                }
                else if (this.priceType == 'stay') {
                    this.priceType = null;
                    this.priceRange = null;
                    this.priceMin = null;
                    this.priceMax = null;
                }
            },
        },

        computed: {
            minPriceLabel() {
                let lbl = 'Minimum';
                
                if (this.priceMin == null) {
                    if (this.uiStore.isMobile) {
                        lbl = 'Min';
                    }
                    lbl += ' '+Formatter.money(this.minMaxRange.start, 0);
                }
                return lbl;
            },

            maxPriceLabel() {
                let lbl = 'Maximum';
                
                if (this.priceMax == null) {
                    if (this.uiStore.isMobile) {
                        lbl = 'Max';
                    }
                    lbl += ' '+Formatter.money(this.minMaxRange.end, 0);
                }
                return lbl;
            },

            numberTicks() {
                if (this.uiStore.isMobile) {
                    return 20;
                }
                return 50;
            },

            ticks() {
                let tickValues = [];
                let min = this.minMaxRange.start;
                let max = this.minMaxRange.end;

                let range = max - min;

                for (let i=0; i<this.numberTicks + 1; i++) {
                    let val = Math.round(min + (range * (i / this.numberTicks)));
                    tickValues.push(val);
                }

                return tickValues;
            },

            tickColors() {

                let colors = [];
                for (let i=0; i<this.ticks.length; i++) {
                    let color = 'primary';
                    if (
                        ((this.priceMin) && (this.priceMin > this.ticks[i])) ||
                        ((this.priceMax) && (this.priceMax < this.ticks[i]))
                    ) {
                        color = 'light';
                    }

                    colors.push(color);
                }

                return colors;
            },

            tickHeights() {
                
                let heights = [];
                let lastTick = 0;
                let maxVal;

                for (let t=0; t<this.ticks.length; t++) {
                    let numInTick = 0;

                    for (let i=0; i<this.properties.length; i++) {

                        let val;
                        if (this.priceType == 'stay') {
                            if (this.quotes) {
                                val = this.quotes[this.properties[i].id];
                            }
                        }
                        else {
                            if (this.quotesNightly) {
                                val = this.quotesNightly[this.properties[i].id];
                            }
                            else if (this.properties[i].avg) {
                                val = Math.round(this.properties[i].avg);
                            }
                        }
                            
                        if (val != null) {
                            if ((val <= this.ticks[t]) && (val > lastTick)) {
                                numInTick++;
                            }
                        }
                    }

                    lastTick = this.ticks[t];
                    heights.push(numInTick);

                    if ((maxVal == null) || (numInTick > maxVal)) {
                        maxVal = numInTick;
                    }
                }

                if ((heights.length == 0) || (!maxVal)) {
                    return null;
                }
                
                for (let i=0; i<heights.length; i++) {
                    heights[i] = Math.round((heights[i] / maxVal) * 100);
                }
                return heights;
            },

            perNightRange() {
                let min;
                let max;

                for (let i=0; i<this.properties.length; i++) {
                    if (this.properties[i].avg != null) {
                        let avg = Math.round(this.properties[i].avg);
                        if ((min == null) || (min > avg)) {
                            min = avg;
                        }
                        if ((max == null) || (max < avg)) {
                            max = avg;
                        }
                    }
                }

                if (min == null) min = 50;
                if (max == null) max = 100000;

                // reset the min/max if we already have
                // search values
                if ((this.priceMin) && (this.priceMin < min)) {
                    min = this.priceMin;
                }
                if ((this.priceMax) && (this.priceMax > max)) {
                    max = this.priceMax;
                }

                // if we don't a range, ignore it
                if (min == max) {
                    return null;
                }

                let range = new Range(min, max);
                range.orient();

                return range;
            },

            perStayRange() {
                return this.quotesRange('quotes', 200, 100000);
            },

            perStayNightlyRange() {
                return this.quotesRange('quotesNightly', 50, 100000);
            },

            minMaxRange() {
                if (this.priceType == 'stay') {
                    return this.perStayRange;
                }
                if (this.quotesNightly) {
                    return this.perStayNightlyRange;
                }
                return this.perNightRange;
            }
        },

        methods: {

            quotesRange(key, defaultMin, defaultMax) {
                let min;
                let max;

                if (this[key]) {
                    let values = Object.values(this[key]);
                    for (let i=0; i<values.length; i++) {
                        if ((min == null) || (min > values[i])) {
                            min = values[i];
                        }
                        if ((max == null) || (max < values[i])) {
                            max = values[i];
                        }
                    }
                }

                if (min == null) min = defaultMin;
                if (max == null) max = defaultMax;

                // reset the min/max if we already have
                // search values
                if ((this.priceMin) && (this.priceMin < min)) {
                    min = this.priceMin;
                }
                if ((this.priceMax) && (this.priceMax > max)) {
                    max = this.priceMax;
                }
                // if we don't a range, ignore it
                if (min == max) {
                    return null;
                }

                let range = new Range(min, max);
                range.orient();

                return range;
            },

            setSearchData() {
                if (this.modelValue) {
                    this.priceType = this.modelValue.priceRangeType;

                    if ((!this.quotes) && (this.priceType == 'stay')) {
                        this.priceType = null;
                    }

                    if ((this.quotes) && (this.priceType == null)) {
                        this.priceType = 'stay';
                    }
                    
                    this.priceRange = this.modelValue.priceRange;
                    if (!this.priceRange) {
                        this.resetValue++;
                    }
                    this.priceRangeChange();
                }
                else {
                    this.priceType = null;
                    this.priceRange = null;

                    if (this.quotes) {
                        this.priceType = 'stay';
                        this.resetValue++;
                    }
                    this.priceRangeChange();
                }
            },

            setDefaultPriceType() {
                if (!this.priceType) {
                    if (this.quotes) {
                        this.priceType = 'stay';
                    }
                    else {
                        this.priceType = 'night';
                    }
                }
            },

            resetPricing() {
                this.priceRange = null;
                this.priceMin = null;
                this.priceMax = null;

                if ((this.priceType == null) && (this.quotes)) {
                    this.priceType = 'stay';
                }

                this.resetValue++;

                this.emitUpdate();
            },

            priceMinChange() {
                if (this.priceMin != null) {
                    if (!this.priceRange) {
                        this.priceRange = this.minMaxRange.copy();
                    }

                    this.priceRange.start = this.priceMin;
                    this.setDefaultPriceType();
                }
                else {
                    this.priceRange = null;
                }

                this.emitUpdate();
            },

            priceMaxChange() {
                if (this.priceMax != null) {
                    if (!this.priceRange) {
                        this.priceRange = this.minMaxRange.copy();
                    }

                    this.priceRange.end = this.priceMax;
                    this.setDefaultPriceType();
                }
                else {
                    this.priceRange = null;
                }

                this.emitUpdate();
            },

            inputPriceRangeChange() {
                this.priceRangeChange();
                this.emitUpdate();
            },

            priceRangeChange() {
                if (this.priceRange) {
                    this.priceMin = this.priceRange.start;
                    this.priceMax = this.priceRange.end;
                    this.setDefaultPriceType();
                }
                else {
                    this.priceMin = null;
                    this.priceMax = null;
                }
            },

            setFromIndex(index) {
                if (!this.priceRange) {
                    this.priceRange = this.minMaxRange.copy();
                }

                let val = this.ticks[index];
                let deltaMin = Math.abs(val - this.priceRange.start);
                let deltaMax = Math.abs(this.priceRange.end - val);

                if (deltaMax < deltaMin) {
                    this.priceRange.end = val;
                }
                else {
                    this.priceRange.start = val;
                }
                this.priceRange.orient();
                this.priceRangeChange();
            },

            emitUpdate() {
                let data = {};
                // only add the pricing data when the range
                // when the price range is inside the rnage
                if (
                    (this.priceRange) &&
                    (
                        (this.priceRange.start > this.minMaxRange.start) ||
                        (this.priceRange.end < this.minMaxRange.end)
                    )
                ) {
                    data.priceRange = this.priceRange.copy();

                    if (this.priceType) {
                        data.priceRangeType = this.priceType;
                    }
                }
                
                
                this.$emit('update:modelValue', data);
            },
        }

    };

</script>
